import { Column, Table } from '@tanstack/react-table'
import { format } from 'date-fns'
import { es } from 'date-fns/locale/es'
import { useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { CalendarIcon } from './DateRangePicker'
import { Button } from './Button'
import { Calendar } from './Calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RangeSlider } from './RangeSlider'
import { OrderBy } from './OrderBy'
import { SelectSearchFilter } from './SelectSearchFilter'
import { HeaderCellOptionsProps } from './table.types'
import { Search } from './Search'
import { Cross2Icon } from '@radix-ui/react-icons'

const FilterIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="12"
      height="10"
      viewBox="0 0 12 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M11 0.5H1L5 5.23V8.5L7 9.5V5.23L11 0.5Z"
        strokeWidth="0.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

interface HeaderCellProps<TData, TValue> extends HeaderCellOptionsProps {
  column: Column<TData, TValue>
  table?: Table<TData>
}

function HeaderCell<TData, TValue>({
  column,
  dateFilter = false,
  description = '',
  formatLabel = (value) => value.toString(),
  orderBy = false,
  rangeFilter = false,
  rangeValues,
  searchFilter = false,
  selectOptions = [],
  selectSearchFilter = false,
  table,
  title = 'Filtro',
  titleGroup = false,
}: HeaderCellProps<TData, TValue>) {
  const [values, setValues] = useState<string | string[] | number[]>([])
  const [isOpen, setOpen] = useState(false)
  const firstValue = table?.getPreFilteredRowModel().flatRows[0]?.getValue(column.id) ?? undefined
  const getPreValues = column.getFilterValue()

  const auxDate = dateFilter ? (column.getFilterValue() as DateRange) : undefined
  const [dateGlobal, setDateGlobal] = useState<DateRange | undefined>({
    from: auxDate?.from || undefined,
    to: auxDate?.to || undefined,
  })

  useEffect(() => {
    if (dateFilter) {
      setDateGlobal({
        from: auxDate?.from || undefined,
        to: auxDate?.to || undefined,
      })
    }
  }, [auxDate, dateFilter])

  useEffect(() => {
    setValues(column.getFilterValue() as string[] | number[] | string)
  }, [getPreValues, column])

  const sortedUniqueValues = useMemo(() => {
    if (selectOptions.length > 0) return []
    return Array.from(column.getFacetedUniqueValues().keys()).sort()
  }, [column, selectOptions])

  const options = selectSearchFilter
    ? selectOptions?.length > 0
      ? selectOptions
      : sortedUniqueValues.map((value) => ({
          label: value.toString(),
          value: value.toString().toLowerCase(),
        }))
    : []

  const filterIconStroke =
    isOpen || column.getIsFiltered() ? 'stroke-[#333333] ' : 'stroke-[#4F4F4F]'

  const hasFilter = selectSearchFilter || rangeFilter || dateFilter || searchFilter
  const colorText = titleGroup ? 'text-black font-semibold' : 'font-normal'

  return orderBy || hasFilter ? (
    <div className="flex min-w-fit items-center gap-1 whitespace-nowrap">
      <div className={colorText}>{(column.columnDef.header as string) || ''}</div>
      <div className="flex items-center">
        {orderBy && column.getCanSort() && <OrderBy column={column} />}
        {hasFilter ? (
          <Popover onOpenChange={setOpen} open={isOpen}>
            <PopoverTrigger className="h-fit rounded-md p-2 hover:bg-[#F2F2F2]">
              <FilterIcon className={filterIconStroke} />
            </PopoverTrigger>
            <PopoverContent className="bg-white">
              <div className="flex flex-col">
                <div className="text-[15px] font-semibold">{title}</div>
                {description && <div className="mt-2 text-[14px] font-medium">{description}</div>}
                {rangeFilter && typeof firstValue === 'number' && (
                  <RangeSlider
                    className="mb-6 mt-4"
                    formatLabel={formatLabel}
                    max={rangeValues?.max || Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
                    min={rangeValues?.min || Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
                    onValueChange={(value) => setValues(value)}
                    step={1}
                    value={column.getFilterValue() as number[]}
                  />
                )}
                {selectSearchFilter && (
                  <SelectSearchFilter
                    className="mb-4 mt-2"
                    multiple
                    onSelect={(value) => {
                      setValues(value)
                    }}
                    options={options}
                    preValues={new Set(column.getFilterValue() as string[])}
                  />
                )}
                {searchFilter && (
                  <Search
                    className="mb-6 mt-4"
                    defaultValue={column.getFilterValue() as string}
                    onChange={(value) => setValues(value)}
                  />
                )}
                {dateFilter && (
                  <>
                    <div className="mt-4 flex h-fit w-full items-center border-b pb-1 text-[14px] font-light">
                      <CalendarIcon className="mr-2 h-4 w-4 fill-[#BDBDBD]" />
                      {dateGlobal?.from ? (
                        dateGlobal.to ? (
                          <>
                            {format(dateGlobal.from, 'd MMM y', { locale: es })} -{' '}
                            {format(dateGlobal.to, 'd MMM y', { locale: es })}
                          </>
                        ) : (
                          format(dateGlobal.from, 'd MMM y', { locale: es })
                        )
                      ) : (
                        <span>Selecciona un período</span>
                      )}
                      {dateGlobal?.from && (
                        <div className="flex ml-auto">
                          <Cross2Icon
                            onClick={(event: React.MouseEvent) => {
                              event?.preventDefault()
                              setDateGlobal({ from: undefined, to: undefined })
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="mb-6 mt-4 flex items-center justify-center">
                      <Calendar
                        captionLayout="dropdown-buttons"
                        className="p-0"
                        defaultMonth={dateGlobal?.from}
                        initialFocus
                        locale={es}
                        mode="range"
                        numberOfMonths={1}
                        onSelect={setDateGlobal}
                        selected={dateGlobal}
                        fromYear={2000}
                        toYear={2030}
                      />
                    </div>
                  </>
                )}
                <div className="mt-4 flex w-full items-center justify-center">
                  <Button
                    className="w-fit px-8"
                    onClick={() => {
                      if (dateFilter) column.setFilterValue(dateGlobal)
                      else column.setFilterValue(values)
                      setOpen(false)
                    }}
                    variant="default"
                  >
                    Aplicar
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    </div>
  ) : (
    <div className={colorText}>{(column.columnDef.header as string) || ''}</div>
  )
}

export { HeaderCell }
